import { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Empty, Radio, Spin, Table, Tabs } from "antd";
import {
  CheckCircleOutlined,
  DownloadOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ordersApi } from "../api/orders";
import { PageHeader } from "../components/PageHeader";
import { formatINR } from "../utils/format";

const { RangePicker } = DatePicker;

const C = {
  brand: "#2563eb",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  slate: "#94a3b8",
  grid: "#eef2f6",
  axis: "#94a3b8",
};

interface Overview {
  total_orders: number;
  total_value: string;
  avg_order_value: number;
  acceptance_rate: number;
  by_status: { accepted: number; rejected: number; cancelled: number; pending: number };
  by_day: { day: string; count: number }[];
  by_hour: { hour: string | null; count: number }[];
}

interface EmployeeRow {
  id: string;
  name: string;
  email: string;
  orders: number;
  total_value: string;
}

interface ShopRow {
  id: string;
  name: string;
  orders: number;
  total_value: string;
}

type Preset = "today" | "week" | "month" | "last" | "custom";

function presetRange(preset: Preset): [Dayjs, Dayjs] | null {
  const now = dayjs();
  switch (preset) {
    case "today":
      return [now.startOf("day"), now.endOf("day")];
    case "week":
      return [now.startOf("week"), now.endOf("week")];
    case "month":
      return [now.startOf("month"), now.endOf("month")];
    case "last":
      return [now.subtract(1, "month").startOf("month"), now.subtract(1, "month").endOf("month")];
    default:
      return null;
  }
}

// --- shared chart bits ------------------------------------------------------
type TipPoint = { value: number; name?: string };
function ChartTooltip({
  active,
  payload,
  label,
  fmt,
}: {
  active?: boolean;
  payload?: TipPoint[];
  label?: string;
  fmt?: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 6px 16px -6px rgba(15,23,42,0.18)",
        fontSize: 12,
      }}
    >
      {label !== undefined && <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>{label}</div>}
      <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
        {fmt ? fmt(payload[0].value) : payload[0].value}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  children,
  style,
}: {
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="card card-pad" style={style}>
      <div className="card-title">{title}</div>
      {children}
    </div>
  );
}

export function Reports() {
  const [preset, setPreset] = useState<Preset>("month");
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [byEmp, setByEmp] = useState<EmployeeRow[]>([]);
  const [byShop, setByShop] = useState<ShopRow[]>([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    if (preset === "custom") return custom;
    return presetRange(preset);
  }, [preset, custom]);

  const params = useMemo(() => {
    if (!range) return {};
    return { from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") };
  }, [range]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      ordersApi.overview(params),
      ordersApi.byEmployee(params),
      ordersApi.byShop(params),
    ])
      .then(([ov, e, s]) => {
        setOverview(ov as Overview);
        setByEmp(e as EmployeeRow[]);
        setByShop(s as ShopRow[]);
      })
      .finally(() => setLoading(false));
  }, [params]);

  return (
    <div className="page-shell">
      <PageHeader title="Reports" subtitle="Analyze orders, employees, and shop performance" />

      <div
        style={{
          background: "white",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          padding: 14,
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <Radio.Group value={preset} onChange={(e) => setPreset(e.target.value)} optionType="button">
          <Radio.Button value="today">Today</Radio.Button>
          <Radio.Button value="week">This Week</Radio.Button>
          <Radio.Button value="month">This Month</Radio.Button>
          <Radio.Button value="last">Last Month</Radio.Button>
          <Radio.Button value="custom">Custom</Radio.Button>
        </Radio.Group>
        {preset === "custom" && (
          <RangePicker value={custom} onChange={(v) => setCustom(v as [Dayjs, Dayjs] | null)} />
        )}
        <div style={{ marginLeft: "auto" }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => window.open(ordersApi.exportUrl(params as Record<string, string>), "_blank")}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {loading || !overview ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
            <Kpi
              icon={<ShoppingCartOutlined />}
              tint={C.brand}
              label="Total Orders"
              value={overview.total_orders.toLocaleString()}
            />
            <Kpi
              icon={<WalletOutlined />}
              tint={C.success}
              label="Total Value"
              value={formatINR(overview.total_value)}
            />
            <Kpi
              icon={<RiseOutlined />}
              tint={C.warning}
              label="Avg Order Value"
              value={formatINR(overview.avg_order_value)}
            />
            <Kpi
              icon={<CheckCircleOutlined />}
              tint={C.success}
              label="Acceptance Rate"
              value={`${overview.acceptance_rate.toFixed(0)}%`}
            />
          </div>

          <Tabs
            items={[
              {
                key: "overview",
                label: "Overview",
                children: <OverviewCharts overview={overview} />,
              },
              {
                key: "emp",
                label: "By Employee",
                children: (
                  <RankedSection
                    title="Top employees by order value"
                    rows={byEmp}
                    columns={[
                      { title: "Employee", dataIndex: "name" },
                      { title: "Email", dataIndex: "email", responsive: ["md"] },
                      { title: "Orders", dataIndex: "orders", align: "right", width: 100 },
                      {
                        title: "Total Value",
                        dataIndex: "total_value",
                        align: "right",
                        width: 140,
                        render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
                      },
                    ]}
                  />
                ),
              },
              {
                key: "shop",
                label: "By Shop",
                children: (
                  <RankedSection
                    title="Top shops by order value"
                    rows={byShop}
                    columns={[
                      { title: "Shop", dataIndex: "name" },
                      { title: "Orders", dataIndex: "orders", align: "right", width: 100 },
                      {
                        title: "Total Value",
                        dataIndex: "total_value",
                        align: "right",
                        width: 140,
                        render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

function Kpi({
  icon,
  tint,
  label,
  value,
}: {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
}) {
  return (
    <div className="card card-pad">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="kpi-icon" style={{ background: `${tint}1A`, color: tint }}>
          {icon}
        </span>
        <div className="kpi-label">{label}</div>
      </div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function OverviewCharts({ overview }: { overview: Overview }) {
  const dayData = overview.by_day.slice(-30).map((d) => ({
    day: dayjs(d.day).format("MMM D"),
    orders: d.count,
  }));

  const hours = new Array(24).fill(0) as number[];
  overview.by_hour.forEach((h) => {
    if (h.hour) hours[dayjs(h.hour).hour()] += h.count;
  });
  const hourData = hours.map((v, i) => ({ hour: `${i}`, orders: v }));

  const bs = overview.by_status;
  const total = bs.accepted + bs.rejected + bs.cancelled + bs.pending;
  const statusData = [
    { name: "Accepted", value: bs.accepted, color: C.success },
    { name: "Pending", value: bs.pending, color: C.warning },
    { name: "Rejected", value: bs.rejected, color: C.danger },
    { name: "Cancelled", value: bs.cancelled, color: C.slate },
  ].filter((d) => d.value > 0);

  return (
    <>
      <ChartCard title="Orders submitted" style={{ marginBottom: 16 }}>
        {dayData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data in this range" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dayData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.brand} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={C.brand} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke={C.grid} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} minTickGap={28} />
              <YAxis tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} allowDecimals={false} width={34} />
              <RTooltip content={<ChartTooltip fmt={(v) => `${v} orders`} />} cursor={{ stroke: C.grid }} />
              <Area
                type="monotone"
                dataKey="orders"
                stroke={C.brand}
                strokeWidth={2}
                fill="url(#gOrders)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <ChartCard title="Orders by status">
          {total === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ position: "relative", width: 180, height: 180, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {statusData.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Pie>
                    <RTooltip content={<ChartTooltip fmt={(v) => `${v} orders`} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>{total}</div>
                  <div className="small-muted">orders</div>
                </div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                {statusData.map((s) => (
                  <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                    <span style={{ flex: 1, color: "var(--text-secondary)" }}>{s.name}</span>
                    <span className="tabular" style={{ fontWeight: 600 }}>{s.value}</span>
                    <span className="small-muted" style={{ width: 44, textAlign: "right" }}>
                      {((s.value / total) * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Orders by hour of day">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={hourData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={C.grid} />
              <XAxis
                dataKey="hour"
                tick={{ fontSize: 10, fill: C.axis }}
                axisLine={false}
                tickLine={false}
                interval={2}
                tickFormatter={(h) => `${h}h`}
              />
              <YAxis tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
              <RTooltip
                content={<ChartTooltip fmt={(v) => `${v} orders`} />}
                cursor={{ fill: "rgba(37,99,235,0.06)" }}
              />
              <Bar dataKey="orders" fill={C.brand} radius={[3, 3, 0, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function RankedSection({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: (EmployeeRow | ShopRow)[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
}) {
  const bars = rows
    .slice()
    .sort((a, b) => Number(b.total_value) - Number(a.total_value))
    .slice(0, 8)
    .map((r) => ({ name: r.name, value: Number(r.total_value) }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ChartCard title={title}>
        {bars.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data in this range" />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, bars.length * 38)}>
            <BarChart data={bars} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke={C.grid} />
              <XAxis type="number" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v)} />
              <YAxis
                type="category"
                dataKey="name"
                width={140}
                tick={{ fontSize: 12, fill: "#475569" }}
                axisLine={false}
                tickLine={false}
              />
              <RTooltip content={<ChartTooltip fmt={(v) => formatINR(v)} />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
              <Bar dataKey="value" fill={C.brand} radius={[0, 4, 4, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <Table rowKey="id" size="middle" dataSource={rows} columns={columns} pagination={false} scroll={{ x: 480 }} />
    </div>
  );
}
